import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import Sidebar from './Sidebar';
import Icon from './Icon';
import NotificationBell from './NotificationBell';
import AssistantWidget from './AssistantWidget';
import MoreMenu from './MoreMenu';
import { useAuth } from '../lib/AuthContext';
import { useI18n } from '../lib/i18n';

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Skupljen meni (samo ikonice) — pamti se između otvaranja aplikacije
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('finart_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    try { localStorage.setItem('finart_sidebar_collapsed', c ? '0' : '1'); } catch { /* ignore */ }
    return !c;
  });
  const { user, signOut } = useAuth();
  const { t, tr } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  // Meni ispod imena gore desno (podešavanja i odjava)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  const { agencies, activeAgencyId, activeAgency, profile } = useAgency();

  // "Više" na telefonu: ceo ekran sa profilom i menijem (zatvara se pri promeni strane)
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // Broj faktura koje čekaju naplatu — za oznaku na kartici "Fakture" u donjoj traci (telefon)
  const [unpaid, setUnpaid] = useState(0);
  useEffect(() => {
    if (!activeAgencyId) { setUnpaid(0); return; }
    let cancelled = false;
    supabase
      .from('invoices')
      .select('id', { count: 'exact', head: true })
      .eq('agency_id', activeAgencyId)
      .neq('document_kind', 'profaktura')
      .in('status', ['poslato', 'delimicno'])
      .then(({ count }) => { if (!cancelled) setUnpaid(count || 0); });
    return () => { cancelled = true; };
  }, [activeAgencyId, location.pathname]);

  // Ime i prezime korisnika: "Ime i prezime obveznika" iz naloga označenog zvezdicom (Moja agencija)
  const [ownerName, setOwnerName] = useState('');
  const [ownerCompany, setOwnerCompany] = useState('');
  // Firma sa zvezdicom; ako korisnik ima samo jednu firmu (npr. glumac), uzima se ona
  const primaryId = (agencies || []).find((a) => a.is_primary)?.id || ((agencies || []).length === 1 ? agencies[0].id : null);
  useEffect(() => {
    if (!primaryId) { setOwnerName(''); setOwnerCompany(''); return; }
    let cancelled = false;
    supabase.from('agency_profile').select('owner_name, company_name').eq('agency_id', primaryId).maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setOwnerName((data?.owner_name || '').trim());
        setOwnerCompany((data?.company_name || '').trim());
      });
    return () => { cancelled = true; };
  }, [primaryId]);
  const displayName = ownerName || user?.email || '';

  // Gore levo: logo i naziv agencije koja je trenutno otvorena (kao u aplikaciji banke)
  const agencyTitle = profile?.company_name || activeAgency?.name || displayName;
  const agencyLogo = activeAgencyId ? profile?.logo_data : null;
  const agencyInitials = String(agencyTitle || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

  // Telefon: strane iz menija "Više" dobijaju gore strelicu nazad i naslov (kao u aplikaciji banke)
  const SUB_PAGES = {
    '/profil': t('userProfile'),
    '/agencija': tr('Podaci agencije', 'Agency details', 'Данные агентства'),
    '/podesavanja/notifikacije': tr('Notifikacije', 'Notifications', 'Уведомления'),
    '/preporuke': t('referrals'),
    '/podrska': tr('Pomoć i podrška', 'Help & support', 'Помощь и поддержка'),
    '/proizvodi': t('navProducts'),
    '/kpo-knjiga': t('navKpoBook'),
    '/poreski-kalendar': t('navTaxCalendar'),
    '/kursna-lista': t('navExchangeList'),
    '/fiskalna-kasa': t('navFiscalCash'),
  };
  const subPath = Object.keys(SUB_PAGES).find((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
  const subTitle = subPath ? SUB_PAGES[subPath] : '';
  const goBack = () => {
    // Ako je strana otvorena direktno (nema prethodne), vrati na Početnu
    if (location.key === 'default') navigate('/');
    else navigate(-1);
  };

  const tabs = [
    { to: '/', icon: 'home', label: t('navHome'), end: true },
    { to: '/fakturisanje', icon: 'receipt', label: tr('Fakture', 'Invoices', 'Счета'), count: unpaid },
    { to: '/klijenti', icon: 'users', label: t('clients') },
    { to: '/porezi', icon: 'percent', label: tr('Porezi', 'Taxes', 'Налоги') },
  ];

  const menuItems = [
    { to: '/profil', icon: 'user', label: t('userProfile') },
    { to: '/agencija', icon: 'building', label: tr('Podaci agencije', 'Agency details', 'Данные агентства') },
    { to: '/podesavanja/notifikacije', icon: 'bell', label: tr('Notifikacije', 'Notifications', 'Уведомления') },
    { to: '/preporuke', icon: 'gift', label: t('referrals') },
  ];

  return (
    <div style={styles.shell}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
      />

      <div style={styles.main} className="app-main">
        <header style={styles.topbar} className={`no-print app-topbar${subTitle ? ' has-sub' : ''}`}>
          {subTitle && (
            <div style={styles.subHead} className="sub-head">
              <button onClick={goBack} style={styles.subBack} aria-label={tr('Nazad', 'Back', 'Назад')}>
                <Icon name="arrowLeft" size={20} />
              </button>
              <span style={styles.subTitle}>{subTitle}</span>
              <span style={{ width: 44, flexShrink: 0 }} />
            </div>
          )}
          <button onClick={() => setSidebarOpen(true)} style={styles.menuBtn} aria-label={t('menuAriaLabel')} className="sidebar-menu-btn">
            <Icon name="menu" size={18} />
          </button>
          <div style={styles.welcome} className="welcome-block">
            {agencyLogo
              ? <img src={agencyLogo} alt="" style={styles.welcomeLogo} />
              : <span style={styles.welcomeBadge}>{agencyInitials}</span>}
            <span style={styles.welcomeText}>
              <span style={styles.welcomeHi}>{tr('Dobro došli', 'Welcome', 'Добро пожаловать')}</span>
              <span style={styles.welcomeName}>{agencyTitle}</span>
            </span>
          </div>
          <div style={styles.userWrap} ref={menuRef} className="user-wrap">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              style={{ ...styles.userChip, ...(menuOpen ? styles.userChipOpen : {}) }}
              className="user-chip"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span style={styles.userDot}>{(displayName || '?')[0].toUpperCase()}</span>
              <span style={styles.userEmail} className="user-chip-name">{displayName}</span>
              <Icon name="chevronsUpDown" size={14} color="var(--ink-500)" />
            </button>

            {menuOpen && (
              <div style={styles.menu} role="menu">
                <div style={styles.menuHead}>
                  <div style={styles.menuName}>{displayName}</div>
                  {ownerName && <div style={styles.menuEmail}>{user?.email}</div>}
                </div>
                {menuItems.map((m) => (
                  <button key={m.to} role="menuitem" onClick={() => navigate(m.to)} style={styles.menuItem} className="user-menu-item">
                    <Icon name={m.icon} size={16} />
                    {m.label}
                  </button>
                ))}
                <div style={styles.menuDivider} />
                <button role="menuitem" onClick={signOut} style={styles.menuItem} className="user-menu-item">
                  <Icon name="logout" size={16} />
                  {t('logout')}
                </button>
              </div>
            )}
          </div>
          <span className="bell-wrap" style={{ display: 'flex' }}><NotificationBell /></span>
        </header>

        <main style={styles.content} className="app-content">{children}</main>
      </div>

      {/* Donja traka sa karticama — samo na telefonu i tabletu */}
      <nav style={styles.tabbar} className={`no-print mobile-tabbar${moreOpen ? ' more-open' : ''}`} aria-label={tr('Glavni meni', 'Main menu', 'Главное меню')}>
        {tabs.map((tb) => (
          <NavLink
            key={tb.to}
            to={tb.to}
            end={tb.end}
            className="tab-item"
            onClick={() => setMoreOpen(false)}
            style={({ isActive }) => ({ ...styles.tab, ...(isActive && !moreOpen ? styles.tabActive : {}) })}
          >
            <span style={styles.tabIconWrap}>
              <Icon name={tb.icon} size={22} />
              {tb.count > 0 && <span style={styles.tabCount}>{tb.count > 99 ? '99+' : tb.count}</span>}
            </span>
            <span style={styles.tabLabel}>{tb.label}</span>
          </NavLink>
        ))}
        <button
          onClick={() => setMoreOpen((o) => !o)}
          className="tab-item"
          aria-expanded={moreOpen}
          style={{ ...styles.tab, ...(moreOpen ? styles.tabActive : {}) }}
          aria-label={tr('Više: ceo meni', 'More: full menu', 'Ещё: всё меню')}
        >
          <span style={styles.tabIconWrap}><Icon name="menu" size={22} /></span>
          <span style={styles.tabLabel}>{tr('Više', 'More', 'Ещё')}</span>
        </button>
      </nav>

      <MoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        displayName={ownerName}
        companyName={ownerCompany}
        unpaid={unpaid}
      />

      <AssistantWidget />

      <style>{`
        @media (min-width: 1024px) {
          .app-main { margin-left: ${collapsed ? 84 : 264}px; transition: margin-left 0.2s ease; }
          /* Gornja traka: iste boje kao pozadina strane (stapa se), ali neprozirna — sadržaj klizi ISPOD nje */
          .app-topbar { background: rgba(242,245,250,0.94) !important; border-bottom: none !important; backdrop-filter: blur(10px) !important; -webkit-backdrop-filter: blur(10px) !important; }
          .sidebar-menu-btn { display: none !important; }
          .mobile-tabbar { display: none !important; }
        }
        /* Telefon i tablet: donja traka umesto dugmeta ☰, sadržaj se ne skriva ispod trake */
        @media (max-width: 1023px) {
          .sidebar-menu-btn { display: none !important; }
          .app-content { padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important; overflow-x: hidden; }
          /* Telefon: gore samo naziv agencije i zvonce; profil i odjava su u "Više" */
          .user-wrap { display: none !important; }
          .app-topbar { padding: 12px 16px !important; background: var(--surface) !important; }
          /* Strane iz "Više": strelica nazad + naslov umesto "Dobro došli" i zvonca */
          .app-topbar.has-sub .welcome-block, .app-topbar.has-sub .bell-wrap { display: none !important; }
          .app-topbar.has-sub .sub-head { display: flex !important; }
        }
        @media print { .mobile-tabbar { display: none !important; } }
        .tab-item { -webkit-tap-highlight-color: transparent; }
        .tab-item:focus:not(:focus-visible) { outline: none; }
        .tab-item[aria-current="page"] svg { stroke: var(--lavender-700); stroke-width: 2.2; }
        /* Dok je "Više" otvoreno, označena je samo kartica "Više" */
        .mobile-tabbar.more-open .tab-item[aria-current="page"] svg { stroke: currentColor; stroke-width: 2; }
        @media (max-width: 480px) { .user-chip-name { display: none; } }
        .user-chip:hover { background: var(--surface); }
        .user-menu-item:hover { background: var(--paper); }
        .user-chip:focus:not(:focus-visible), .user-menu-item:focus:not(:focus-visible) { outline: none; }
      `}</style>
    </div>
  );
}

const styles = {
  shell: { minHeight: '100vh', background: 'var(--paper)' },
  main: { minHeight: '100vh' },
  topbar: {
    // Gornja traka bez boje — od sadržaja je odvaja samo tanka linija
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 24px', background: 'rgba(246,246,243,0.92)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, zIndex: 20,
  },
  menuBtn: {
    border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 10,
    width: 40, height: 40, color: 'var(--teal-800)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  welcome: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 },
  subHead: { display: 'none', flex: 1, alignItems: 'center', gap: 10, minWidth: 0 },
  subBack: {
    width: 44, height: 44, flexShrink: 0, borderRadius: '50%', border: 'none', background: 'var(--paper)',
    color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  },
  subTitle: { flex: 1, minWidth: 0, textAlign: 'center', fontSize: 17, fontWeight: 800, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  welcomeBadge: {
    width: 48, height: 48, borderRadius: '50%', background: 'var(--teal-800)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800,
    letterSpacing: '0.02em', flexShrink: 0,
  },
  welcomeLogo: {
    width: 48, height: 48, borderRadius: '50%', objectFit: 'contain', background: '#fff',
    border: '1px solid var(--line)', flexShrink: 0,
  },
  welcomeText: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  welcomeHi: { fontSize: 16, fontWeight: 700, color: 'var(--ink-900)' },
  welcomeName: {
    fontSize: 12, fontWeight: 600, lineHeight: 1.3, color: 'var(--ink-500)', textTransform: 'uppercase',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },
  userWrap: { position: 'relative', minWidth: 0 },
  userChip: {
    display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, border: '1px solid transparent',
    background: 'transparent', borderRadius: 10, padding: '5px 8px', cursor: 'pointer',
  },
  userChipOpen: { background: 'var(--surface)', borderColor: 'var(--line)' },
  menu: {
    position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 240, background: 'var(--surface)',
    border: '1px solid var(--line)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: 6, zIndex: 40,
  },
  menuHead: { padding: '8px 10px 10px', borderBottom: '1px solid var(--line)', marginBottom: 4 },
  menuName: { fontSize: 14, fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  menuEmail: { fontSize: 12, color: 'var(--ink-500)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  menuItem: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', background: 'transparent',
    borderRadius: 9, padding: '10px', fontSize: 14, fontWeight: 500, color: 'var(--ink-700)', textAlign: 'left', cursor: 'pointer',
  },
  menuDivider: { height: 1, background: 'var(--line)', margin: '4px 0' },
  userDot: {
    width: 28, height: 28, borderRadius: '50%', background: 'var(--lavender-100)', color: 'var(--lavender-700)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  userEmail: {
    fontSize: 13, color: 'var(--ink-500)', maxWidth: 220, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  content: { padding: '28px 20px 60px', maxWidth: 1100, margin: '0 auto' },
  tabbar: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 25, display: 'flex',
    background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
    borderTop: '1px solid var(--line)', padding: '6px 4px calc(6px + env(safe-area-inset-bottom, 0px))',
  },
  tab: {
    flex: 1, minWidth: 0, minHeight: 52, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 3, border: 'none', background: 'transparent', color: 'var(--ink-500)', textDecoration: 'none',
    fontFamily: 'inherit', cursor: 'pointer', padding: 0,
  },
  tabActive: { color: 'var(--lavender-700)' },
  tabIconWrap: { position: 'relative', display: 'flex' },
  tabCount: {
    position: 'absolute', top: -6, left: 14, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999,
    background: 'var(--teal-800)', color: '#fff', fontSize: 10, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--surface)',
  },
  tabLabel: { fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' },
};
